import { useEffect, useState } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import {
  Settings as SettingsIcon,
  ChatBubbleOutline,
  CreditCard,
  HelpOutline,
  ExitToApp,
  DevicesOther,
  PeopleOutline,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { fetchPrompts } from "../../redux/actions/userAction";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";
import { tracker } from "../../analytics";
import BillingInfo from "./components/BillingInfo/BillingInfo";
import DeviceManagement from "./components/DeviceManagement/DeviceManagement";
import SubscriptionUpgradeBanner from "../PaymentScreen/components/SubscriptionUpgradeBanner";
import { useSubscription } from "../../contexts/SubscriptionProvider";
import { TrialBanner } from "../../components/TrialBanner";
import SettingsSidebar from "./components/SettingsSidebar/SettingsSidebar";
import GeneralTab from "./components/GeneralTab/GeneralTab";
import PromptsTab from "./components/PromptsTab/PromptsTab";
import SupportTab from "./components/SupportTab/SupportTab";
import LogoutTab from "./components/LogoutTab/LogoutTab";
import UserManagement from "./components/UserManagement/UserManagement";

const SIDEBAR_WIDTH = 320;

const SettingsScreen = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const { subscription } = useSubscription();
  const dispatch = useDispatch();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const user = useSelector((state) => state.auth?.user?.user);

  const isRegularUser =
    user && user.roles && user.roles.length === 1 && user.roles[0] === "user";
  const isAdmin = user && user.roles && user.roles.includes("admin");

  // Build tab IDs sequentially — no collisions regardless of role combination
  let _id = 0;
  const tabIds = {
    general: _id++,         // always 0
    prompts: _id++,         // always 1
    ...(!isRegularUser ? { billing: _id++ } : {}),
    // Gestion des utilisateurs : réservée aux administrateurs de l'organisation
    // (inviter, changer les rôles, retirer). Les endpoints correspondants
    // (api/user/invite, update-roles, users/:id, invitations/*) exigent déjà le
    // rôle « admin » et sont limités à l'organisation de l'appelant.
    ...(isAdmin ? { users: _id++ } : {}),
    deviceManagement: _id++,
    support: _id++,
    logout: _id++,
  };

  const tabs = [
    { id: tabIds.general, label: t("general_parameters"), icon: <SettingsIcon /> },
    { id: tabIds.prompts, label: t("prompts"), icon: <ChatBubbleOutline /> },
    ...(!isRegularUser
      ? [{ id: tabIds.billing, label: t("billing_tab"), icon: <CreditCard /> }]
      : []),
    ...(isAdmin
      ? [
          {
            id: tabIds.users,
            label: t("user_management"),
            icon: <PeopleOutline />,
          },
        ]
      : []),
    { id: tabIds.deviceManagement, label: t("device_management"), icon: <DevicesOther /> },
    { id: tabIds.support, label: t("support"), icon: <HelpOutline /> },
    { id: tabIds.logout, label: t("logout"), icon: <ExitToApp /> },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (isMobile) {
      setMobileOpen(false);
    }

    ReactGA.event({
      category: "Settings",
      action: "Tab Change",
      label: `Tab Index: ${tabId}`,
    });
    tracker.event({
      category: "Settings",
      action: "Tab Change",
      label: `Tab Index: ${tabId}`,
    });
  };

  useEffect(() => {
    dispatch(fetchPrompts(user.id, "organization_prompt"));
  }, [user, dispatch]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        backgroundColor: theme.palette.background.default,
        minHeight: "100vh",
        direction: currentLang === "ar" ? "rtl" : "ltr",
      }}
    >
      <SettingsSidebar
        user={user}
        isAdmin={isAdmin}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        mobileOpen={mobileOpen}
        onDrawerToggle={handleDrawerToggle}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: theme.palette.background.default,
          minHeight: isMobile ? `calc(100vh - 64px)` : "100vh",
          width: isMobile ? "100%" : `calc(100% - ${SIDEBAR_WIDTH}px)`,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {subscription?.status === "trial" && (
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 20,
              px: { xs: 2, sm: 3, md: 2 },
              pt: { xs: 1.5, sm: 2, md: 1.5 },
              pb: 0,
              bgcolor: theme.palette.background.default,
            }}
          >
            <TrialBanner sticky={false} source="Settings" />
          </Box>
        )}

        <Box
          sx={{
            p: { xs: 2, sm: 3, md: 2 },
            pt: subscription?.status === "trial" ? 0 : { xs: 2, sm: 3, md: 2 },
            flex: 1,
          }}
        >
          {subscription &&
            !["active", "trial"].includes(subscription.status) && (
              <SubscriptionUpgradeBanner />
            )}

          {activeTab === tabIds.general && <GeneralTab />}
          {activeTab === tabIds.prompts && <PromptsTab />}
          {!isRegularUser && activeTab === tabIds.billing && <BillingInfo />}
          {isAdmin && activeTab === tabIds.users && <UserManagement />}
          {activeTab === tabIds.deviceManagement && <DeviceManagement />}
          {activeTab === tabIds.support && <SupportTab />}
          {activeTab === tabIds.logout && <LogoutTab />}
        </Box>
      </Box>
    </Box>
  );
};

export default SettingsScreen;
